import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { chartSeriesFor } from '../../../theme/muiTheme';
import { formatNumber } from '../../../shared/utils/number';
import ChartTooltip from './ChartTooltip';

const formatAxis = (value) => (
  Math.abs(value) >= 1000
    ? `${formatNumber(value / 1000, { min: 0, max: 1 })}k`
    : formatNumber(value, { max: 0 })
);

const formatBarLabel = (value) => {
  if (value == null) return '';
  if (Math.abs(value) >= 1000) return `${formatNumber(value / 1000, { min: 1, max: 1 })}k`;
  return formatNumber(value, { min: 2, max: 2 });
};

export default function MonthlyEmissionsChart({ data }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = chartSeriesFor(theme.palette.mode === 'dark');
  const chartData = data.map((item) => ({
    ...item,
    month: t(`dashboard.months.${item.monthKey}`),
  }));

  return (
    <Box
      sx={{
        height: { xs: 310, sm: 340, md: 360, lg: '100%' },
        minHeight: { lg: 360 },
        minWidth: 0,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 8, right: compact ? 0 : 10, left: compact ? -20 : 0, bottom: 0 }}
          barCategoryGap={compact ? '18%' : '24%'}
        >
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            axisLine={{ stroke: theme.palette.divider }}
            tick={{ fill: theme.palette.text.secondary, fontSize: compact ? 11 : 12 }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            axisLine={false}
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            tickFormatter={formatAxis}
            tickLine={false}
            width={compact ? 42 : 52}
          />
          <Tooltip
            cursor={{ fill: theme.palette.action.hover }}
            content={<ChartTooltip totalLabel={t('dashboard.scope.total')} />}
          />
          <Legend
            iconType="square"
            iconSize={9}
            wrapperStyle={{ color: theme.palette.text.secondary, fontSize: 13, paddingTop: 12 }}
          />
          <Bar
            dataKey="scope1"
            name={t('dashboard.scope.scope1')}
            stackId="emissions"
            fill={colors.SCOPE_1}
            maxBarSize={42}
          />
          <Bar
            dataKey="scope2"
            name={t('dashboard.scope.scope2')}
            stackId="emissions"
            fill={colors.SCOPE_2}
            maxBarSize={42}
          />
          <Bar
            dataKey="scope3"
            name={t('dashboard.scope.scope3')}
            stackId="emissions"
            fill={colors.SCOPE_3}
            maxBarSize={42}
            radius={[4, 4, 0, 0]}
          >
            {!compact && (
              <LabelList
                dataKey="total"
                position="top"
                formatter={formatBarLabel}
                style={{
                  fill: theme.palette.text.secondary,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
